import { css } from '@emotion/react'
import {
  Image,
  IndicatedIssueReviseImage,
  IndicatedIssueReviseImageStageEnum,
} from '@ulysses-inc/harami_api_client'
import { Modal, Typography, Upload } from 'antd'
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface'
import React, { useState } from 'react'
import { Lightbox } from 'src/components/lightbox/Lightbox'
import { getMBSize } from 'src/exShared/util/fileSize'
import { ThumbList } from 'src/features/improves/improveDetail/listItem/revisePicturesWall/ThumbList'
import { ModalDeleteButtonColor } from 'src/features/theme/KdsThemeColor'
import { createDefaultFileList } from 'src/libraries/upload/pictures'
import { uploadImagesRequestByFiles } from 'src/state/ducks/images/sagas'
import invariant from 'tiny-invariant'

const { Text } = Typography

export type ReviseFile = {
  stage: IndicatedIssueReviseImageStageEnum
  file: UploadFile<Image>
}

export type ReviseFileList = ReviseFile[]

type Props = {
  setFieldImagesValue: (images: IndicatedIssueReviseImage[]) => void
  addFieldImagesValue: (image: IndicatedIssueReviseImage, index: number) => void
  reviseImages: IndicatedIssueReviseImage[]
  disabled: boolean
  errorMessage?: React.ReactElement
}

/**
 * カイゼン一覧画面のテーブルの「改善画像」列のセル
 */
const RevisePicturesWall: React.FC<Props> = ({
  setFieldImagesValue,
  addFieldImagesValue,
  reviseImages,
  disabled,
  errorMessage,
}) => {
  const images = reviseImages
    .map(image => {
      return (
        image.image && {
          stage: image.stage,
          file: createDefaultFileList([image.image])[0],
        }
      )
    })
    .filter(i => i) as ReviseFileList

  const defaultFileList = reviseImages
    .map(image => image.image && createDefaultFileList([image.image])[0])
    .filter(i => i) as UploadFile[]

  const [fileList, setFileList] = useState<ReviseFileList>(images)
  const [previewImage, setPreviewImage] = useState('')
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewSelectedIndex, setPreviewSelectedIndex] = useState(0)
  const [visiblePreviewModal, setVisiblePreviewModal] = useState(false)
  const [error, setError] = useState('')

  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || '')
    setPreviewVisible(true)
  }

  const handleUploadFile = async (file: UploadFile) => {
    if (!file || !(file instanceof File)) {
      return
    }
    const [uploadImage] = (await uploadImagesRequestByFiles([file])) as Image[]
    return uploadImage
  }

  const handleImageToUploadFile = (image: Image) => {
    // TODO: uploadImagesRequestByFilesでアップロードされた画像のIDがすべて0になってしまいuid = 0で重複するので、代わりのユニークな値をいれる
    const [uploadFile] = createDefaultFileList(
      [image].map(ui => {
        ui.id = new Date().getTime()
        return ui
      }),
    )
    invariant(uploadFile)
    return uploadFile
  }

  const handleChange = async (info: UploadChangeParam) => {
    const newFile = info.file
    const newFileList = info.fileList

    setError('')
    // MARK: 最大容量は決め打ちで 10 MB に設定した
    //       https://kaminashi.atlassian.net/browse/HPB-2307?focusedCommentId=16214

    if (newFile.size && newFile.size > getMBSize(10)) {
      setError('画像ファイルの最大容量は10MBです')
      return
    }

    const tempReviseFile = {
      stage: IndicatedIssueReviseImageStageEnum.Latest,
      file: newFile,
    }
    setFileList(prevFileList => [...prevFileList, tempReviseFile])

    const uploadImage = await handleUploadFile(newFile)
    if (!uploadImage) {
      handleRemove(tempReviseFile)
      return
    }
    const uploadFile = handleImageToUploadFile(uploadImage)
    const index = newFileList.findIndex(file => file.uid === newFile.uid)

    const reviseImage = {
      stage: IndicatedIssueReviseImageStageEnum.Latest,
      image: { uuid: uploadImage.uuid },
    }
    const reviseFile = {
      stage: IndicatedIssueReviseImageStageEnum.Latest,
      file: uploadFile,
    }
    addFieldImagesValue(reviseImage, index)
    setFileList(prevFileList =>
      prevFileList.map(thisFile => {
        if (thisFile.file.uid === newFile.uid) {
          return reviseFile
        }
        return thisFile
      }),
    )
  }

  const handleRemove = (removingFile: ReviseFile) => {
    Modal.confirm({
      onOk: () => {
        setError('')

        // このコンポーネントで保持している画像リストから削除
        const prunedFileList = fileList.filter(
          f => f.file.uid !== removingFile.file.uid,
        )
        setFileList(prunedFileList)

        // 親コンポーネントのFormikが持っている画像リストから削除
        // - 本来は子が知るべきでない情報がここで必要になっているので、改善したいところ
        // - fileListから探すのではなくてreviseImagesから探すべきでは？🤔
        const removingFileIndex = fileList.findIndex(
          f => f.file.uid === removingFile.file.uid,
        )
        const prunedReviseImages = reviseImages.filter(
          (_, index) => index !== removingFileIndex,
        )
        setFieldImagesValue(prunedReviseImages)
      },
      title: '画像の削除を実行しますがよろしいですか？',
      okText: '削除',
      cancelText: 'キャンセル',
      okButtonProps: {
        style: {
          backgroundColor: ModalDeleteButtonColor,
          border: 'none',
        },
      },
    })
  }

  const previewImages = fileList
    .filter(f => f.file.url || f.file.thumbUrl)
    .map(f => f.file.url || f.file.thumbUrl || '')

  return disabled && previewImages.length == 0 ? (
    <div css={styles.noPhotosText}>未登録</div>
  ) : (
    <div css={styles.container}>
      <Upload
        type="drag"
        beforeUpload={() => false}
        multiple={true}
        showUploadList={false}
        fileList={defaultFileList}
        disabled={disabled}
        onPreview={handlePreview}
        accept={'.png,.jpg,.jpeg'}
        onChange={handleChange}
      >
        <div css={styles.placeholder}>
          <div
            className="ant-upload-text"
            style={{
              textAlign: 'center',
              whiteSpace: 'pre-wrap',
            }}
          >
            {'画像を\nドラッグ＆ドロップ'}
          </div>
        </div>
        <ThumbList
          fileList={fileList}
          disabled={disabled}
          handlePreview={(index: number) => {
            setPreviewSelectedIndex(index)
            setVisiblePreviewModal(true)
          }}
          handleRemove={handleRemove}
        />
      </Upload>
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="example" style={{ width: '100%' }} src={previewImage} />
      </Modal>
      <Lightbox
        carousel={{ finite: true }}
        close={() => setVisiblePreviewModal(false)}
        index={previewSelectedIndex}
        open={visiblePreviewModal}
        slides={previewImages.map(image => ({ src: image }))}
      />
      {error ? (
        <Text type="danger" style={{ fontSize: '14px' }}>
          {error}
        </Text>
      ) : (
        errorMessage
      )}
    </div>
  )
}

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    z-index: 1;

    > span:first-child,
    .ant-upload {
      display: block;
      width: 100%;
      height: 100%;
    }

    .ant-upload.ant-upload-drag .ant-upload {
      padding: 0;
    }
  `,
  // おそらく不要
  placeholder: css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    height: 100%;
  `,
  noPhotosText: css`
    padding: 12px 0 0 8px;
    align-self: flex-start;
    color: #d9d9d9;
  `,
}

export default RevisePicturesWall
